import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Teachr from "../../../Objects/models/Teachr";
import { useTailwindBrand } from "../../../Theme/useTailwindBrand";
import type { OnboardingStep } from "../../../Hooks/Dashboard/useHome";
import ButtonField from "../../Ui/ButtonField";
import { capitalizeFirstLetter } from "../../../Utils/Function";
import RewardDialog from "./RewardDialog";
import RemainingStepsDialog from "./RemainingStepsDialog";

export interface OnboardingTabProps {
  teachr?: Teachr;
  totalStudents?: number;
  brandColors: Record<string, string>;
  primaryTextClass?: string;
  steps: OnboardingStep[];
  overallProgress: number;
  currentLevel?: { name: string; emoji: string };
  currentLevelNum?: number;
  nextLevel?: { name: string; emoji: string };
  rewardPdfUrl?: string;
  onViewReward?: () => void;
  onNext?: () => void;
  isLoading?: boolean;
}



const OnboardingTab: React.FC<OnboardingTabProps> = ({
  teachr,
  // brandColors,
  primaryTextClass = "text-brand-primary",
  steps,
  overallProgress,
  currentLevel,
  currentLevelNum = 1,
  // nextLevel - réintégrer à la prochaine version
  rewardPdfUrl,
  onViewReward,
  onNext,
  isLoading = false,
}) => {
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const [rewardOpen, setRewardOpen] = useState(false);
  const [remainingOpen, setRemainingOpen] = useState(false);

  const incompleteSteps = steps.filter((s) => !s.completed);

  const handleAccordionChange =
    (stepId: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedStep(isExpanded ? stepId : null);
    };

  const firstName = teachr?.firstName || "";
  const { brandConfig } = useTailwindBrand();

  useEffect(() => {
    if (onNext) {
      setRewardOpen(true);
      onViewReward?.();
    }
  }, [onNext, onViewReward])

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse p-8">
        <div className="h-10 bg-gray-200 rounded-xl w-1/3" />
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-16 bg-gray-200 rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <Card
        variant="outlined"
        sx={{
          boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <div className="p-6 pb-4">
            <div className="flex items-center gap-3 mb-1">
              <div
                className={`p-2 rounded-md {brandColors.iconBg} border-2 text-2xl shadow-sm`}
              >
                {/* <RocketLaunchIcon className="text-white text-xl" /> */}🚀
              </div>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ fontFamily: brandConfig.fontFamily }}
                className={primaryTextClass}
              >
                Bienvenue, {capitalizeFirstLetter(firstName)} 👋
              </Typography>
            </div>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 7, fontFamily: brandConfig.fontFamily }}>
              Terminez ces étapes pour optimiser votre profil au max de son potentiel !
            </Typography>
          </div>

          <div className="px-6 pb-6">
            <div className="flex items-center justify-between mb-3">
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
                sx={{ fontFamily: brandConfig.fontFamily }}
              >
                Complétion du profil
              </Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ fontFamily: brandConfig.fontFamily }}
              >
                {overallProgress}%
              </Typography>
            </div>
            <div className="h-3 w-full bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full bg-gradient-to-l from-[#219CFF] to-[${brandConfig.colors.primary}] rounded-full transition-all duration-700 ease-out`}
                style={{ width: `${overallProgress}%` }}
              />
            </div>
          </div>

          {/* Liste des étapes en accordéon */}
          <Box sx={{ px: 3, pb: 3, "& > .MuiAccordion-root": { mb: 1.5 } }}>
            {steps.map((step) => {
              const isExpanded = expandedStep === step.id;
              const sub = step.subSteps;
              const statusLabel = step.completed
                ? "Complété"
                : sub
                  ? `${sub.completed}/${sub.total}`
                  : "0/1";
              return (
                <Accordion
                  key={step.id}
                  expanded={isExpanded}
                  onChange={handleAccordionChange(step.id)}
                  disableGutters
                  sx={{
                    "&:before": { display: "none" },
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                    border: "1px solid",
                    borderColor: step.completed ? brandConfig.colors.primary : "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                    backgroundColor: step.completed
                      ? brandConfig.colors.background // version 10% opacity de la couleur primaire
                      : "background.paper",
                    "&.Mui-expanded": { margin: 0, mb: 1.5 },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      "& .MuiAccordionSummary-content": {
                        flex: 1,
                        my: 2,
                        mr: 1,
                        minWidth: 0,
                        alignItems: "flex-start",
                      },
                      "& .MuiAccordionSummary-expandIconWrapper": {
                        ml: 1,
                        mt: 1,
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: { xs: 1.5, sm: 2 }, flex: 1, minWidth: 0 }}>
                      {/* Icône */}
                      <Box
                        sx={{
                          flexShrink: 0,
                          width: { xs: 40, sm: 48 },
                          height: { xs: 40, sm: 48 },
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: step.completed ? "" : "grey.200",
                          color: step.completed ? "" : "grey.600",
                          "& .MuiSvgIcon-root": { fontSize: { xs: 22, sm: 28 } },
                        }}
                      >
                        {step.icon}
                      </Box>
                      {/* Texte + badge mobile */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            sx={{
                              fontFamily: brandConfig.fontFamily,
                              color: step.completed ? brandConfig.colors.primary : "text.primary",
                              flex: 1,
                              minWidth: 0,
                            }}
                            className="font-bold"
                          >
                            {step.title}
                          </Typography>
                          {/* Badge visible uniquement sur mobile */}
                          <Box
                            component="span"
                            sx={{
                              display: { xs: "inline-flex", sm: "none" },
                              alignItems: "center",
                              gap: 0.5,
                              px: 1,
                              py: 0.25,
                              borderRadius: 10,
                              typography: "caption",
                              fontWeight: 600,
                              fontFamily: brandConfig.fontFamily,
                              bgcolor: step.completed ? brandConfig.colors.primary : "grey.200",
                              color: step.completed ? "white" : "grey.700",
                              fontSize: "0.7rem",
                            }}
                          >
                            {step.completed && <CheckCircleIcon sx={{ fontSize: 13 }} />}
                            {statusLabel}
                          </Box>
                        </Box>
                        {step.description && (
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0.25,
                              fontFamily: brandConfig.fontFamily,
                              color: brandConfig.colors.text,
                            }}
                            className="font-semibold"
                          >
                            {step.description}
                          </Typography>
                        )}
                      </Box>
                      {/* Badge visible uniquement sur desktop */}
                      <Box
                        sx={{
                          flexShrink: 0,
                          display: { xs: "none", sm: "flex" },
                          alignItems: "center",
                          gap: 1,
                          ml: 1,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 10,
                            typography: "caption",
                            fontWeight: 600,
                            fontFamily: brandConfig.fontFamily,
                            bgcolor: step.completed ? brandConfig.colors.primary : "grey.200",
                            color: step.completed ? "white" : "grey.700",
                          }}
                        >
                          {step.completed && <CheckCircleIcon sx={{ fontSize: 16 }} />}
                          {statusLabel}
                        </Box>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 2, pl: 3, pr: 2 }}>
                    {step.children && step.children.length > 0 ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {step.children.map((sub) => {
                          const subCompleted = sub.completed;
                          const subLocked = sub.locked ?? false;
                          const subSub = sub.subSteps;
                          const subStatusLabel = subCompleted
                            ? "Complété"
                            : subSub
                              ? `${subSub.completed}/${subSub.total}`
                              : "0/1";
                          return (
                            <Box
                              key={sub.id}
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: subCompleted ? brandConfig.colors.primary : "divider",
                                bgcolor: subCompleted
                                  ? "white"
                                  : "background.paper",

                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                                {/* Icône */}
                                <Box
                                  sx={{
                                    flexShrink: 0,
                                    width: { xs: 36, sm: 40 },
                                    height: { xs: 36, sm: 40 },
                                    borderRadius: 1.5,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: subCompleted ? "" : "grey.200",
                                    color: subCompleted ? "" : "grey.600",
                                  }}
                                >
                                  {sub.icon}
                                </Box>
                                {/* Texte + badge + action */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography variant="subtitle2" fontWeight={600} sx={{ fontFamily: brandConfig.fontFamily, color: subCompleted ? brandConfig.colors.primary : "text.primary" }} className="font-bold">
                                        {sub.title}
                                      </Typography>
                                      {sub.description && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: brandConfig.fontFamily, display: "block", mt: 0.25, color: brandConfig.colors.text }} className="font-semibold">
                                          {sub.description}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 1 }}>
                                      <Box
                                        component="span"
                                        sx={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                          px: 1.5,
                                          py: 0.5,
                                          borderRadius: 10,
                                          typography: "caption",
                                          fontWeight: 600,
                                          fontFamily: brandConfig.fontFamily,
                                          bgcolor: sub.completed ? brandConfig.colors.primary : "grey.200",
                                          color: sub.completed ? "white" : "grey.700",
                                          border: "1px solid",
                                        }}
                                      >
                                        {sub.completed && <CheckCircleIcon sx={{ fontSize: 16 }} />}
                                        {subStatusLabel}
                                      </Box>
                                    </Box>
                                  </Box>
                                  {subLocked ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: brandConfig.fontFamily, mt: 0.5, display: "block" }}>
                                      Débloquez l’étape précédente
                                    </Typography>
                                  ) : !subCompleted && sub.action ? (
                                    <Box sx={{ mt: 1 }}>
                                      <ButtonField
                                        variant="contained"
                                        label="Commencer"
                                        onClick={() => sub.action?.()}
                                      />
                                    </Box>
                                  ) : null}
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : step.completed ? (
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: brandConfig.fontFamily }}>
                        Cette étape est terminée.
                      </Typography>
                    ) : step.action ? (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                          <RadioButtonUncheckedIcon sx={{ fontSize: 22, color: "text.secondary" }} />
                          <Typography variant="body2" sx={{ fontFamily: brandConfig.fontFamily }}>
                            Compléter cette étape pour avancer
                          </Typography>
                        </Box>

                        {step?.locked ? (
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: brandConfig.fontFamily }}>
                            Débloquez l’étape précédente
                          </Typography>
                        ) :
                          <ButtonField
                            variant="contained"
                            label="Commencer"
                            onClick={() => step.action?.()}
                          />
                        }
                      </Box>
                    ) : null}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>

          {/* Footer : niveau suivant - désactivé, à réintégrer à la prochaine version
          {nextLevel && (
            <Box ...>
              ...niveau suivant + bouton récompense...
            </Box>
          )} */}
        </CardContent>
      </Card>
      <RewardDialog
        open={rewardOpen}
        onClose={() => setRewardOpen(false)}
        currentLevelNum={currentLevelNum}
        currentLevel={currentLevel}
        rewardPdfUrl={rewardPdfUrl}
        onNext={onNext}
      />

      <RemainingStepsDialog
        open={remainingOpen}
        onClose={() => setRemainingOpen(false)}
        currentLevelNum={currentLevelNum}
        currentLevel={currentLevel}
        incompleteSteps={incompleteSteps}
      />
    </div>
  );
};

export default OnboardingTab;
